import { ExclamationCircleOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Affix, Typography, Button as AntButton } from "antd";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { ManagePatientDetailsContext } from "./context";
import { font14pxSB } from "../Typography";
import { ConflictType } from "./ConflictType";
import { getDiagnosisConflictPage } from "../../utils/diagnosis";
import { ERROR_25, ERROR_300, ERROR_600, GRAY_500, GRAY_900 } from "../../constant/colors";
import { transparentize } from "polished";

const Text = Typography.Text;

const Button = styled(AntButton)`
  padding: 0;
  width: 1em;
  height: 1em;
  cursor: pointer;
`;

const ConflictNavigatorWrapper = styled.div`
  background: ${transparentize(0.2, ERROR_25)};
  backdrop-filter: blur(7px);
  border: 1px solid ${ERROR_300};
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 16px;
  display: flex;
`;

const flex = css`
  flex: 1;
  display: flex;
  align-items: center;
`;

const LeftAlignFlex = styled.div`
  ${flex}
  text-align: left;
  svg {
    color: ${ERROR_600};
  }
`;

const RightAlignFlex = styled.div`
  ${flex}
  text-align: right;
  justify-content: flex-end;
`;

const ConflictNavigatorTitle = styled(Text)`
  ${font14pxSB}
  color: ${ERROR_600};
  margin-left: 14px;
`;
const ConflictSourceType = styled(Text)`
  ${font14pxSB}
  color: ${({ conflictAlertIndex }) => (conflictAlertIndex !== -1 ? GRAY_500 : "transparent")};
  margin-right: 8px;
`;
const ConflictPager = styled(Text)`
  ${font14pxSB}
  color: ${({ conflictAlertIndex }) => (conflictAlertIndex !== -1 ? GRAY_900 : "transparent")};
  margin-right: 24px;
`;

const ConflictPagerButtonBack = styled.div`
  ${flex}
  color: ${({ conflictAlertIndex }) => (conflictAlertIndex !== -1 ? "black" : "transparent")};
  flex: 0;
  margin-right: 18px;
`;
const ConflictPagerButtonForward = styled.div`
  ${flex}
  color: ${({ conflictAlertIndex, numOfConflict }) =>
    conflictAlertIndex !== numOfConflict - 1 ? "black" : "transparent"};
  flex: 0;
`;

const AffixPaddingContainer = styled.div`
  padding-top: 16px;
`;

const ConflictSourceTypeTitleMap = {
  [ConflictType.diagnosis]: "Diagnosis",
  [ConflictType.demographic]: "Demographic",
  [ConflictType.coverage]: "Coverage"
};

const sectionsAnchorMap = {
  [ConflictType.demographic]: "#manage-patient-details-personal",
  [ConflictType.coverage]: "#manage-patient-details-coverages",
  [ConflictType.diagnosis]: "#manage-patient-details-diagnosis"
};

// Height/weight/allergies -> Medical Information section.
const MEDICAL_DEMOGRAPHIC_KEYS = ["patientHeight", "patientWeight", "patientAllergies"];
const MEDICAL_INFO_ANCHOR = "#manage-patient-details-medical-information";
// Address/phone/email/caregiver fields -> Contact Details card.
const CONTACT_DEMOGRAPHIC_KEYS = [
  "address",
  "city",
  "stateCode",
  "zipCode",
  "homeNumber",
  "phoneNumber",
  "email",
  "fullNameCaregiver",
  "relationToPatientCaregiver",
  "phoneCaregiver",
  "emailCaregiver",
  "alternateContactOption"
];
const CONTACT_ANCHOR = "#manage-patient-contact-details";

const getConflictAnchor = (conflict) => {
  if (!conflict) return null;
  if (conflict.conflictType === ConflictType.demographic) {
    if (MEDICAL_DEMOGRAPHIC_KEYS.includes(conflict.name)) return MEDICAL_INFO_ANCHOR;
    if (CONTACT_DEMOGRAPHIC_KEYS.includes(conflict.name)) return CONTACT_ANCHOR;
  }
  return sectionsAnchorMap[conflict.conflictType];
};

const SCROLL_OFFSET = 80;

const handleScrollTo = (conflict) => {
  const anchorValue = getConflictAnchor(conflict);
  const element = anchorValue && document.getElementById(anchorValue.substring(1));
  const container = document.getElementById("scrollContainer");
  if (element && container) {
    const top =
      element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - SCROLL_OFFSET;
    container.scrollTo({ top, behavior: "smooth" });
  }
};

const prepareConflictsList = (rawConflictsList) => {
  const conflictsBySectionOrderMap = {
    [ConflictType.demographic]: [],
    [ConflictType.coverage]: [],
    [ConflictType.diagnosis]: []
  };

  for (const conflict of rawConflictsList) {
    const _value = JSON.stringify(conflict);
    const sectionConflicts = conflictsBySectionOrderMap[conflict.conflictType];
    const isExist = sectionConflicts.findIndex((obj) => {
      return JSON.stringify(obj) === _value;
    });
    if (isExist === -1) {
      conflictsBySectionOrderMap[conflict.conflictType].push(conflict);
    }
  }
  return Object.values(conflictsBySectionOrderMap).flat();
};

const ConflictNavigator = () => {
  const { conflictsData, diagnosisTableData, setDiagnosisPage } = useContext(ManagePatientDetailsContext);
  const { t } = useTranslation();
  const [conflictsList, setConflictsList] = useState([]);
  // Local index spans all conflict types, including diagnosis rows on a page the table isn't showing.
  const [currentIndex, setCurrentIndex] = useState(-1);

  useEffect(() => {
    if (conflictsData.length === 0) {
      setConflictsList([]);
      setCurrentIndex(-1);
    } else {
      const conflictsBySectionOrder = prepareConflictsList(conflictsData);
      setConflictsList(conflictsBySectionOrder);
      setCurrentIndex((i) => Math.min(i, conflictsBySectionOrder.length - 1));
    }
  }, [conflictsData]);

  const currentConflictType = conflictsList[currentIndex]?.conflictType;
  const conflictSourceTypeTitle = ConflictSourceTypeTitleMap[currentConflictType] || "";

  const navigateToConflict = (conflict) => {
    if (conflict?.conflictType === ConflictType.diagnosis) {
      const page = getDiagnosisConflictPage(diagnosisTableData, conflict.key);
      if (page) setDiagnosisPage(page);
    }
    handleScrollTo(conflict);
  };

  const goToIndex = (index) => {
    setCurrentIndex(index);
    navigateToConflict(conflictsList[index]);
  };
  const onClickBack = () => {
    if (currentIndex > 0) goToIndex(currentIndex - 1);
  };
  const onClickNext = () => {
    if (currentIndex < conflictsList.length - 1) goToIndex(currentIndex + 1);
  };

  if (!conflictsData?.length) {
    return null;
  }

  return (
    <Affix target={() => document.getElementById("scrollContainer")} offsetTop={0}>
      <AffixPaddingContainer>
        <ConflictNavigatorWrapper>
          <LeftAlignFlex>
            <ExclamationCircleOutlined />
            <ConflictNavigatorTitle>
              {conflictsList.length} {t("managePatientDetails.conflicts_detected")}
            </ConflictNavigatorTitle>
          </LeftAlignFlex>
          <RightAlignFlex>
            <ConflictSourceType conflictAlertIndex={currentIndex}>{conflictSourceTypeTitle}</ConflictSourceType>
            <ConflictPager conflictAlertIndex={currentIndex}>
              {currentIndex + 1}/{conflictsList.length}
            </ConflictPager>
            <Button type="text" onClick={onClickBack}>
              <ConflictPagerButtonBack conflictAlertIndex={currentIndex}>
                <LeftOutlined />
              </ConflictPagerButtonBack>
            </Button>
            <Button type="text" onClick={onClickNext}>
              <ConflictPagerButtonForward conflictAlertIndex={currentIndex} numOfConflict={conflictsList.length}>
                <RightOutlined />
              </ConflictPagerButtonForward>
            </Button>
          </RightAlignFlex>
        </ConflictNavigatorWrapper>
      </AffixPaddingContainer>
    </Affix>
  );
};

export { ConflictNavigator };
